import React, { useState, useRef, useEffect, useMemo, useContext } from "react";
import { useRouter } from "@/shared/lib/router/router";
import { useTranslation } from "react-i18next";
import {
  Search, Loader2, CornerDownLeft, X,
  LayoutDashboard, Users, Briefcase, BarChart3, Archive, Ticket, MessagesSquare, Mail, Settings, Smartphone, AlertTriangle, User, Rocket, LayoutGrid,
  type LucideIcon,
} from "lucide-react";
import { Modal } from "@/shared/ui/modal";
import { authContext } from "@/shared/model/authContext";
import { ROUTES } from "@/shared/config/routes";
import { PERMISSIONS } from "@/shared/config/permissions";
import { useContractedModules } from "@/entities/session/model/contractedModules";
import { isAppBuildsEnabled } from "@/entities/app-build/lib/appBuildEnvironment";
import { projectService } from "@/entities/project/api/projectService";

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Cmd {
  id: string;
  label: string;
  hint?: string;
  href: string;
  icon: LucideIcon;
  group: "Navegação" | "Obras";
}

export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const { t } = useTranslation("common");
  const router = useRouter();
  const { checkPermission } = useContext(authContext);
  const isContracted = useContractedModules();

  const [query, setQuery] = useState("");
  const [projects, setProjects] = useState<{ id: string; title: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  // Comandos de navegação filtrados por permissão (espelha a sidebar).
  const navCommands = useMemo<Cmd[]>(() => {
    const can = (p: string) => checkPermission(p);
    const all: Array<Cmd & { show: boolean }> = [
      { id: "home", label: "Início", href: ROUTES.home, icon: LayoutDashboard, group: "Navegação", show: true },
      {
        id: "users", label: "Registos", href: ROUTES.users, icon: Users, group: "Navegação",
        show: can(PERMISSIONS.CAN_LIST_USERS) || can(PERMISSIONS.CAN_LIST_SYSTEM_PROFILES) || can(PERMISSIONS.CAN_LIST_SUPPLIERS) || can(PERMISSIONS.CAN_LIST_COLLABORATORS),
      },
      {
        id: "projects", label: "Obras", href: `${ROUTES.projects}?tab=list`, icon: Briefcase, group: "Navegação",
        show: can(PERMISSIONS.CAN_LIST_PROJECTS) || can(PERMISSIONS.CAN_LIST_PARTICIPATING_PROJECTS),
      },
      {
        id: "financial", label: "Financeiro", href: ROUTES.financial, icon: BarChart3, group: "Navegação",
        show: isContracted("financial") && (can(PERMISSIONS.CAN_LIST_COSTS) || can(PERMISSIONS.CAN_LIST_OFFER_COSTS)),
      },
      {
        id: "inventory", label: "Inventário", href: ROUTES.storages, icon: Archive, group: "Navegação",
        show: isContracted("inventory") && (can(PERMISSIONS.CAN_LIST_STORAGES) || can(PERMISSIONS.CAN_LIST_CATEGORIES) || can(PERMISSIONS.CAN_LIST_OFFERS) || can(PERMISSIONS.CAN_LIST_TOOLS) || can(PERMISSIONS.CAN_LIST_STOCK_TOOLS) || can(PERMISSIONS.CAN_LIST_STOCK_BALANCES) || can(PERMISSIONS.CAN_LIST_STOCKS) || can(PERMISSIONS.CAN_LIST_ACTION_HISTORY) || can(PERMISSIONS.CAN_LIST_SOLICITATIONS)),
      },
      { id: "tickets", label: "Chamados", href: ROUTES.tickets, icon: Ticket, group: "Navegação", show: isContracted("tickets") },
      { id: "crm", label: "Mensagens", href: ROUTES.crmChat, icon: MessagesSquare, group: "Navegação", show: isContracted("crm") },
      {
        id: "contacts", label: "Contactos", href: ROUTES.contacts, icon: Mail, group: "Navegação",
        show: isContracted("contact") && (can(PERMISSIONS.CAN_LIST_CONTACTS) || can(PERMISSIONS.CAN_LIST_SUBMITS) || can(PERMISSIONS.CAN_APPROVE_SUBMIT)),
      },
      {
        id: "settings", label: "Definições", href: ROUTES.settings, icon: Settings, group: "Navegação",
        show: can(PERMISSIONS.CAN_LIST_SYSTEM_PROFILES) || can(PERMISSIONS.CAN_LIST_ROLES) || can(PERMISSIONS.CAN_LIST_PERMISSION) || can(PERMISSIONS.CAN_LIST_STEP_NAMES) || can(PERMISSIONS.CAN_LIST_TASK_NAMES),
      },
      {
        id: "app-hub", label: t("sidebar.appHub") || "Centro de entregas", href: ROUTES.adminAppHub, icon: LayoutGrid, group: "Navegação",
        show: isContracted("appHub") && (can(PERMISSIONS.CAN_LIST_APP_BUILDS) || can(PERMISSIONS.CAN_USE_APP_DEVICE)),
      },

      {
        id: "errors", label: t("sidebar.errors") || "Rastreamento de Erros", href: ROUTES.adminErrors, icon: AlertTriangle, group: "Navegação",
        show: can(PERMISSIONS.CAN_VIEW_ERRORS),
      },
      { id: "me", label: "Meu perfil", href: ROUTES.me, icon: User, group: "Navegação", show: true },
    ];
    return all.filter((c) => c.show) as Cmd[];
  }, [checkPermission, isContracted]);

  useEffect(() => {
    if (isOpen) {
      setQuery(""); setActiveIdx(0); setProjects([]);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [isOpen]);

  const canListProjects = checkPermission(PERMISSIONS.CAN_LIST_PROJECTS) || checkPermission(PERMISSIONS.CAN_LIST_PARTICIPATING_PROJECTS);

  // Pesquisa de obras (debounced, tolerante a falha).
  useEffect(() => {
    if (!isOpen || !canListProjects) return;
    let cancelled = false;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await projectService.getAll({
          page: 1, size: 6,
          filter: query.trim() ? { title: query.trim() } : undefined,
        });
        if (!cancelled) setProjects((data.content || []).map((p) => ({ id: p.id, title: p.title })));
      } catch {
        if (!cancelled) setProjects([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [query, isOpen, canListProjects]);

  const filteredNav = useMemo<Cmd[]>(() => {
    const q = query.trim().toLowerCase();
    return q ? navCommands.filter((c) => c.label.toLowerCase().includes(q)) : navCommands;
  }, [navCommands, query]);

  const projectResults = useMemo<Cmd[]>(
    () => projects.map((p) => ({ id: `proj:${p.id}`, label: p.title || "Obra", href: ROUTES.project(p.id), icon: Briefcase, group: "Obras" as const })),
    [projects],
  );

  const results = useMemo(() => [...filteredNav, ...projectResults], [filteredNav, projectResults]);

  useEffect(() => { setActiveIdx(0); }, [query, projects.length]);

  // Acompanha o item ativo na navegação por teclado (rola se estiver fora da vista).
  useEffect(() => { activeRef.current?.scrollIntoView({ block: "nearest" }); }, [activeIdx]);

  const go = (cmd?: Cmd) => {
    const target = cmd || results[activeIdx];
    if (!target) return;
    onClose();
    router.push(target.href);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(); }
    else if (e.key === "Escape") { e.preventDefault(); onClose(); }
  };

  let runningIdx = -1;
  const groups: Array<Cmd["group"]> = ["Navegação", "Obras"];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" maxWidth="max-w-2xl" noPadding clean>
      <div className="bg-night_input border border-white/10 rounded-lg overflow-hidden shadow-modal animate-in zoom-in-95 duration-base">
        {/* Input */}
        <div className="flex items-center px-4 py-3 border-b border-white/10 gap-3">
          {loading
            ? <Loader2 size={20} className="text-yellow animate-spin" />
            : <Search size={20} className="text-yellow" />}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t("header.search")}
            className="flex-1 bg-transparent border-none outline-none text-white placeholder-white/30 text-lg"
          />
          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-white/10 bg-white/5 dark:bg-night_surface/5 text-[10px] text-white/70 uppercase font-bold tracking-widest">
            <span>{t("nav.search.esc")}</span>
          </div>
          <button onClick={onClose} className="p-1 text-white/70 hover:text-white transition-colors ml-1">
            <X size={20} />
          </button>
        </div>

        {/* Resultados */}
        <div className="min-h-[300px] max-h-[55vh] overflow-y-auto sidebar-scrollbar py-2">
          {results.length === 0 ? (
            <div className="p-12 text-center text-white/70 italic">
              {t("nav.search.noResultsFor", { query })}
            </div>
          ) : (
            groups.map((g) => {
              const rows = results.filter((r) => r.group === g);
              if (rows.length === 0) return null;
              return (
                <div key={g}>
                  <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-white/40">{g}</p>
                  {rows.map((r) => {
                    runningIdx += 1;
                    const active = runningIdx === activeIdx;
                    const Icon = r.icon;
                    return (
                      <button
                        key={r.id}
                        ref={active ? activeRef : undefined}
                        onMouseEnter={() => setActiveIdx(results.indexOf(r))}
                        onClick={() => go(r)}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors ${active ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}
                      >
                        <span className="flex items-center gap-3 min-w-0">
                          <Icon size={18} className="shrink-0 text-yellow" />
                          <span className="truncate">{r.label}</span>
                        </span>
                        {active && <CornerDownLeft size={14} className="text-white/40 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Footer com atalhos */}
        <div className="px-4 py-3 bg-black/20 border-t border-white/5 flex items-center justify-between text-[11px] text-white/70 uppercase font-bold tracking-widest">
          <div className="flex gap-4">
            <span className="flex items-center gap-1"><span className="px-1 py-0.5 rounded bg-white/10 dark:bg-night_surface/10 text-white">↵</span> {t("nav.search.select")}</span>
            <span className="flex items-center gap-1"><span className="px-1 py-0.5 rounded bg-white/10 dark:bg-night_surface/10 text-white">↑↓</span> {t("nav.search.navigate")}</span>
          </div>
        </div>
      </div>
    </Modal>
  );
}
